import _ from 'lodash';
import React from 'react';
import { useMutation } from 'react-relay';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import {
    composerQueriesCreateDesignTemplateMutation,
    DesignInput,
    DesignItemConstraintsInput
} from '../__generated__/composerQueriesCreateDesignTemplateMutation.graphql';
import { composerQueriesUpdateDesignTemplateMutation } from '../__generated__/composerQueriesUpdateDesignTemplateMutation.graphql';
import { composerCreateDesignTemplateMutation, composerUpdateDesignTemplateMutation } from '../composerQueries';

// Save design
const useSaveDesign = () => {
    const [zkProduct] = usePreview3DStore((store) => [store.zkProduct]);

    const [createDesign] = useMutation<composerQueriesCreateDesignTemplateMutation>(composerCreateDesignTemplateMutation);
    const [updateDesign] = useMutation<composerQueriesUpdateDesignTemplateMutation>(composerUpdateDesignTemplateMutation);
    const [isDesignSaving, setIsDesignSaving] = React.useState(false);

    const mapDesignToInput = (design: MPlaza.Design): DesignInput => ({
        designID: design.get('designID') ?? 0,
        docID: design.get('docID') ?? undefined,
        modelID: design.get('modelID'),
        modelName: design.get('modelName') ?? undefined,
        modelCode: zkProduct?.get('code') ?? undefined,
        pricingModelID: design.get('pricingModelID'),
        modelPriceDeltaPerc: design.get('modelPriceDeltaPerc'),
        modelPriceDeltaValue: design.get('modelPriceDeltaValue'),
        modelPrice: design.get('modelPrice'),
        canBeSetAsDefaultTemplate: false,
        modelTextBasePrice: design.get('modelTextBasePrice'),
        modelImageBasePrice: design.get('modelImageBasePrice'),
        printTypeID: design.get('printTypeID'),
        description: design.get('description') ?? undefined,
        name: design.get('name') ?? undefined,
        isTemplate: design.get('isTemplate'),
        isDraft: design.get('isDraft'),
        templateDesignID: design.get('templateDesignID'),
        customerID: design.get('customerID'),
        customerCode: undefined,
        visitorID: design.get('visitorID'),
        visitorCode: undefined,
        finalPrice: design.get('finalPrice'),
        tempPreviewImageData: design.get('tempPreviewImageData') ?? undefined,
        tempPreviewImageUrl: design.get('tempPreviewImageUrl') ?? undefined,
        canAddText: design.get('canAddText'),
        canAddImage: design.get('canAddImage'),
        canAddShape: true,
        maxNrTexts: design.get('maxNrTexts') ?? undefined,
        maxNrImages: design.get('maxNrImages') ?? undefined,
        maxNrShapes: undefined,
        filterCode: design.get('filterCode') ?? undefined,
        canChangeSvgColors: design.get('canChangeSvgColors'),
        canMaskImages: false,
        uploadRestrictions: design.get('uploadRestrictions') ?? undefined,
        disableSellerImages: design.get('disableSellerImages') ?? false,
        hasSyncEnabled: design.get('hasSyncEnabled'),
        salesChannelID: 0,
        createdBy: 0,
        createdOn: undefined,
        modifiedBy: 0,
        modifiedOn: undefined,
        isDeleted: false,
        isLibrary: false,
        compositionID: 0,
        compositionDocID: undefined,
        compoundKey: undefined,
        currentModificationID: undefined,
        pdSettings: 0,
        interfaceID: undefined,
        marketPlaceOrderID: undefined,
        parentTemplateId: undefined,
        templateCode: undefined,
        adaptItemsToVariants: 'NONE',
        sides: design.get('sides').map((side) => ({
            sideID: side.get('sideID'),
            colorID: side.get('colorID'),
            modelID: side.get('modelID'),
            fillColor: side.get('fillColor') ?? undefined,
            canAddText: side.get('canAddText'),
            canAddImage: side.get('canAddImage'),
            canAddShape: true,
            canMaskImages: false,
            MaxNrTexts: side.get('maxNrTexts') ?? undefined,
            MaxNrImages: side.get('maxNrImages') ?? undefined,
            MaxNrShapes: undefined,
            canChangeSvgColors: side.get('canChangeSvgColors'),
            disableSellerImages: side.get('disableSellerImages'),
            uploadRestrictions: (side.get('uploadRestrictions') as any)?.toJSON()
        })),
        sidesPrintTypes: [],
        areasGrids: [],
        areas: [],
        designItems: design.get('designItems').map((item) => ({
            itemID: item.get('itemID') ?? 0,
            itemGuid: item.get('itemGuid'),
            index: item.get('index'),
            name: item.get('name') ?? undefined,
            json: item.get('json') ?? undefined,
            svg: item.get('svg') ?? undefined,
            imageID: item.get('imageID') ?? undefined,
            imageType: item.get('imageType') ?? undefined,
            imageFormat: item.get('imageFormat') ?? undefined,
            imageWidth: item.get('imageWidth') ?? undefined,
            imageHeight: item.get('imageHeight') ?? undefined,
            imageDpiX: item.get('imageDpiX') ?? undefined,
            imageDpiY: item.get('imageDpiY') ?? undefined,
            imageIsMulticolor: item.get('imageIsMulticolor') ?? false,
            imagePreferredWidth: item.get('imagePreferredWidth') ?? undefined,
            imagePreferredHeight: item.get('imagePreferredHeight') ?? undefined,
            imageFileSize: item.get('imageFileSize') ?? undefined,
            imagePrice: item.get('imagePrice') ?? undefined,
            imageCustomerCode: undefined,
            imageVisitorCode: undefined,
            imageSellerID: 0,
            imageCustomerID: item.get('imageCustomerID') ?? undefined,
            imageVisitorID: item.get('imageVisitorID') ?? undefined,
            providerImageID: undefined,
            designItemTagTypeID: undefined,
            applyImagePriceForQuantity: undefined,
            itemColors: item.get('itemColors').map((ic) => {
                if (!ic) return null;
                if (ic instanceof Object === false) return ic;
                return ic.get('colorCode') ?? null;
            }),
            areas: item.get('areas').map((area) => ({
                modelID: area.get('modelID'),
                areaID: area.get('areaID'),
                areaGuid: undefined,
                sideID: area.get('sideID'),
                colorID: area.get('colorID')
            })),
            colors: item.get('colors').map((color) => ({
                colorID: color.get('colorID'),
                colorCode: color.get('colorCode') ?? undefined,
                imageID: color.get('imageID') ?? undefined,
                itemID: color.get('itemID') ?? undefined
            })),
            constraints:
                item.get('constraints')?.toJSON() &&
                _.extend(_.omit<DesignItemConstraintsInput>(item.get('constraints')?.toJSON(), ['canMoveX', 'canMoveY']), {
                    isAlwaysOnBottom: false
                }),
            defaultLCID: 0,
            designItemTagType: 'NONE'
        })),

        categories: [],
        tags: design.get('tags') ?? undefined,
        clientPreviews: [],
        colors: [],
        canvasRotation: undefined,
        options: [],
        modifications: [],
        elementsDescs: undefined,
        designTempPreviews: [],
        additionalAttributes: []
    });

    const saveDesign = async (libraryDesign: MPlaza.Design) => {
        setIsDesignSaving(true);
        try {
            const designID = libraryDesign.getDesignID();
            const design = mapDesignToInput(libraryDesign);
            await new Promise<void>((resolve, reject) => {
                if (designID) {
                    updateDesign({
                        variables: {
                            design: design
                        },
                        onCompleted: (response, errors) => {
                            if (errors && errors.length > 0) {
                                reject(errors);
                            } else {
                                resolve();
                            }
                        },
                        onError: (err) => {
                            reject(err);
                        }
                    });
                } else {
                    createDesign({
                        variables: {
                            design: design
                        },
                        onCompleted: (response, errors) => {
                            if (errors && errors.length > 0) {
                                reject(errors);
                            } else {
                                resolve();
                            }
                        },
                        onError: (err) => {
                            reject(err);
                        }
                    });
                }
            }).catch((error) => {
                throw error;
            });
        } catch (error) {
            console.error('useSaveDesign: Error while saving design:', error);
            throw error;
        } finally {
            setIsDesignSaving(false);
        }
    };

    return [saveDesign, isDesignSaving] as const;
};

export default useSaveDesign;
